import { useEffect, useState, useCallback } from "react";
import { getEXPOs, uploadEXPOs, deleteEXPO, previewEXInvoice, generateEXInvoice, getEXInvoicePDF, updateEXPOStatus, bulkGenerateEXInvoices, bulkCloseEXPOs, getEXStores, getEXInventory, createEXManualPO } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Upload, FileText, Trash2, Printer, Download, CheckCircle, XCircle, AlertTriangle, Clock, X, RefreshCw, Zap, CheckCheck, Plus, Package, Store, Search } from "lucide-react";
import { toast } from "sonner";

export default function EXPOProcessing() {
  const [pos, setPOs] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [showManualPO, setShowManualPO] = useState(false);

  const loadPOs = useCallback(async () => {
    try { const r = await getEXPOs(); setPOs(r.data); } catch { toast.error("Failed to load EXTREME POs"); }
    setLoading(false);
  }, []);

  useEffect(() => { loadPOs(); }, [loadPOs]);

  const pendingCount = pos.filter(p => p.status === "pending").length;
  const invoicedCount = pos.filter(p => p.status === "invoiced").length;

  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const r = await uploadEXPOs(Array.from(files));
      const ok = r.data.results.filter(r => r.status === "success").length;
      if (ok) toast.success(`Uploaded ${ok} EXTREME PO(s)`);
      await loadPOs();
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const handleSelect = async (po) => {
    setSelectedPO(po); setPreview(null);
    if (po.status === "pending") {
      try { const r = await previewEXInvoice(po.id); setPreview(r.data); } catch { toast.error("Preview failed"); }
    }
  };

  const handleGenerate = async () => {
    if (!selectedPO) return;
    setGenerating(true);
    try {
      const r = await generateEXInvoice(selectedPO.id);
      toast.success(`Invoice ${r.data.invoice_number} generated`);
      await loadPOs(); setSelectedPO({...selectedPO, status: "invoiced", invoice_id: r.data.id}); setPreview(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setGenerating(false);
  };

  const handleBulkGenerate = async () => {
    if (!window.confirm(`Generate invoices for ${pendingCount} pending EXTREME POs?`)) return;
    setBulkGenerating(true);
    try {
      const r = await bulkGenerateEXInvoices({ po_ids: pos.filter(p => p.status === "pending").map(p => p.id) });
      toast.success(`Generated ${r.data.successful}/${r.data.total_processed}`);
      await loadPOs(); setSelectedPO(null);
    } catch { toast.error("Bulk generate failed"); }
    setBulkGenerating(false);
  };

  const handlePrint = async () => {
    if (!selectedPO?.invoice_id) return;
    try {
      const r = await getEXInvoicePDF(selectedPO.invoice_id);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url)?.print?.();
    } catch { toast.error("Print failed"); }
  };

  const getStatusBadge = (status) => {
    if (status === "pending") return <Badge className="bg-amber-100 text-amber-900"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    if (status === "invoiced") return <Badge className="bg-emerald-100 text-emerald-900"><CheckCircle className="w-3 h-3 mr-1" />Invoiced</Badge>;
    return <Badge variant="outline"><XCircle className="w-3 h-3 mr-1" />{status}</Badge>;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="ex-po-processing">
      <Card><CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700">Upload EXTREME PO PDFs</h3>
          <Button size="sm" variant="outline" onClick={() => setShowManualPO(true)} data-testid="ex-manual-po-btn"><Plus className="w-4 h-4 mr-1.5" />Create Manual PO</Button>
        </div>
        <div className="upload-zone rounded-lg p-6 text-center cursor-pointer" onClick={() => document.getElementById("ap-po-upload").click()}>
          <Input id="ex-po-upload" type="file" multiple accept=".pdf" className="hidden" onChange={e => handleUpload(e.target.files)} />
          {uploading ? <p className="text-slate-600">Uploading...</p> : <><Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" /><p className="text-sm text-slate-600">Drop EXTREME PO PDFs here or click to upload</p></>}
        </div>
      </CardContent></Card>

      {(pendingCount > 0 || invoicedCount > 0) && (
        <Card className="bg-slate-900 text-white"><CardContent className="p-4 flex items-center justify-between">
          <div><h3 className="font-semibold">Bulk Actions</h3><p className="text-slate-400 text-sm">{pendingCount} pending, {invoicedCount} invoiced</p></div>
          <div className="flex gap-2">
            <Button onClick={handleBulkGenerate} disabled={bulkGenerating || !pendingCount} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ex-bulk-generate">
              {bulkGenerating ? "Generating..." : <><Zap className="w-4 h-4 mr-1" />Generate All ({pendingCount})</>}
            </Button>
          </div>
        </CardContent></Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" style={{ minHeight: "calc(100vh - 22rem)" }}>
        <Card className="lg:col-span-4 overflow-hidden">
          <CardHeader className="py-3 px-4 border-b"><CardTitle className="text-sm">EXTREME Purchase Orders ({pos.length})</CardTitle></CardHeader>
          <ScrollArea className="h-[450px]">
            {pos.length === 0 ? <div className="p-8 text-center text-slate-500"><FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" /><p>No EXTREME POs</p></div> : (
              <div className="divide-y divide-slate-100">
                {pos.map(po => (
                  <div key={po.id} className={`p-4 cursor-pointer po-item ${selectedPO?.id === po.id ? "active" : ""}`} onClick={() => handleSelect(po)} data-testid={`ap-po-${po.id}`}>
                    <div className="flex items-start justify-between">
                      <div><p className="font-medium font-mono text-sm">{po.po_number}</p><p className="text-xs text-slate-500 mt-1">{po.buyer_store || po.buyer_name}</p></div>
                      <div className="flex flex-col items-end gap-2">
                        {getStatusBadge(po.status)}
                        {po.status === "pending" && <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400 hover:text-red-500" onClick={e => { e.stopPropagation(); deleteEXPO(po.id).then(() => { toast.success("Deleted"); loadPOs(); }); }}><Trash2 className="w-4 h-4" /></Button>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </Card>

        <Card className="lg:col-span-8 overflow-hidden">
          {!selectedPO ? (
            <div className="h-full flex items-center justify-center text-slate-500 p-8"><div className="text-center"><FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" /><p>Select an EXTREME PO to view details</p></div></div>
          ) : (
            <>
              <CardHeader className="py-3 px-4 border-b flex flex-row items-center justify-between">
                <div><CardTitle className="text-lg font-mono">{selectedPO.po_number}</CardTitle><p className="text-sm text-slate-500">{selectedPO.buyer_store}</p></div>
                <div className="flex items-center gap-2">
                  {selectedPO.status === "pending" && <Button onClick={handleGenerate} disabled={generating || !preview?.items?.some(i => i.allocated)} data-testid="ex-generate-btn">{generating ? "Generating..." : <><CheckCircle className="w-4 h-4 mr-2" />Generate Invoice</>}</Button>}
                  {selectedPO.status === "invoiced" && <Button onClick={handlePrint}><Printer className="w-4 h-4 mr-2" />Print</Button>}
                </div>
              </CardHeader>
              <ScrollArea className="h-[400px]">
                <div className="p-6">
                  {preview && selectedPO.status === "pending" && (
                    <div className="border rounded-lg p-6 bg-white">
                      <Table>
                        <TableHeader><TableRow className="bg-slate-900 hover:bg-slate-900">
                          <TableHead className="text-white text-xs">SR</TableHead><TableHead className="text-white text-xs">PRODUCT</TableHead>
                          <TableHead className="text-white text-xs text-center">QTY</TableHead><TableHead className="text-white text-xs text-right">MRP</TableHead>
                          <TableHead className="text-white text-xs text-right">UNIT</TableHead><TableHead className="text-white text-xs text-right">TOTAL</TableHead>
                          <TableHead className="text-white text-xs text-center">STATUS</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {preview.items.map((item, idx) => (
                            <TableRow key={idx} className={item.allocated ? "bg-emerald-50" : item.matched ? "bg-amber-50" : "bg-red-50"}>
                              <TableCell className="text-xs">{item.allocated ? item.sr_no : "-"}</TableCell>
                              <TableCell className="text-sm font-medium">{item.product_name}</TableCell>
                              <TableCell className="text-center font-mono">{item.allocated ? item.quantity : 0}</TableCell>
                              <TableCell className="text-right font-mono">Rs.{item.mrp?.toLocaleString()}</TableCell>
                              <TableCell className="text-right font-mono">Rs.{item.unit_price?.toLocaleString()}</TableCell>
                              <TableCell className="text-right font-mono">Rs.{item.line_total?.toLocaleString()}</TableCell>
                              <TableCell className="text-center">
                                {!item.matched ? <Badge className="bg-red-100 text-red-700 text-xs">Not Found</Badge> : !item.allocated ? <Badge className="bg-amber-100 text-amber-700 text-xs">Out of Stock</Badge> : <Badge className="bg-emerald-100 text-emerald-700 text-xs">Allocated</Badge>}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <div className="mt-4 flex justify-end"><div className="w-60 space-y-1">
                        <div className="flex justify-between text-sm"><span>Taxable:</span><span className="font-mono">Rs.{preview.taxable_amount?.toLocaleString()}</span></div>
                        <div className="flex justify-between font-bold text-lg border-t pt-2"><span>Grand Total:</span><span className="font-mono">Rs.{preview.grand_total?.toLocaleString()}</span></div>
                      </div></div>
                    </div>
                  )}
                  {(selectedPO.status === "invoiced" || selectedPO.status === "closed") && (
                    <div className="text-center py-12"><CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" /><p className="text-xl font-semibold">Invoice Generated</p></div>
                  )}
                </div>
              </ScrollArea>
            </>
          )}
        </Card>
      </div>

      {showManualPO && <EXManualPOModal onClose={() => setShowManualPO(false)} onCreated={() => { setShowManualPO(false); loadPOs(); }} />}
    </div>
  );
}

function EXManualPOModal({ onClose, onCreated }) {
  const [stores, setStores] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [selectedStore, setSelectedStore] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getEXStores(), getEXInventory()])
      .then(([s, i]) => { setStores(s.data.stores || []); setInventory((i.data || []).filter(x => x.quantity > 0)); })
      .catch(() => toast.error("Failed to load data"))
      .finally(() => setLoading(false));
  }, []);

  const toggleItem = (item) => setSelectedItems(prev => prev.find(i => i.sr_no === item.sr_no) ? prev.filter(i => i.sr_no !== item.sr_no) : [...prev, {...item, quantity: 1}]);

  const handleCreate = async () => {
    if (!selectedStore || !selectedItems.length) return;
    setCreating(true);
    try {
      const r = await createEXManualPO({ store_name: selectedStore, items: selectedItems.map(i => ({ model_name: i.model_name, quantity: i.quantity, mrp: i.mrp, unit_price: i.unit_price || i.mrp / 2 })) });
      toast.success(r.data.message); onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setCreating(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="ex-manual-po-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold">Create EXTREME Manual PO</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        {loading ? <div className="p-16 text-center"><div className="spinner" /></div> : (
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div>
              <label className="text-sm font-semibold mb-2 block"><Store className="w-4 h-4 inline mr-1" />Store</label>
              <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                {stores.map(s => (
                  <button key={s.store_name} onClick={() => setSelectedStore(s.store_name)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium border ${selectedStore === s.store_name ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200"}`}>
                    {s.store_name}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold mb-2 block"><Package className="w-4 h-4 inline mr-1" />Items ({selectedItems.length} selected)</label>
              <div className="border rounded-lg max-h-48 overflow-y-auto">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50 text-xs"><TableHead></TableHead><TableHead>Product</TableHead><TableHead className="text-right">Stock</TableHead><TableHead className="text-right">MRP</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {inventory.slice(0, 80).map(item => {
                      const sel = selectedItems.some(i => i.sr_no === item.sr_no);
                      return (
                        <TableRow key={item.sr_no} className={`cursor-pointer text-xs ${sel ? "bg-blue-50" : "hover:bg-slate-50"}`} onClick={() => toggleItem(item)}>
                          <TableCell><div className={`w-4 h-4 rounded border flex items-center justify-center ${sel ? "bg-blue-600 border-blue-600 text-white" : "border-slate-300"}`}>{sel && <CheckCircle className="w-3 h-3" />}</div></TableCell>
                          <TableCell className="font-medium">{item.model_name}</TableCell>
                          <TableCell className="text-right font-mono">{item.quantity}</TableCell>
                          <TableCell className="text-right font-mono">Rs.{item.mrp?.toLocaleString()}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )}
        <div className="px-6 py-4 border-t bg-slate-50 flex justify-between">
          <span className="text-sm text-slate-600">{selectedItems.length} items selected</span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating || !selectedStore || !selectedItems.length} data-testid="ex-create-manual-po">
              {creating ? "Creating..." : <><Plus className="w-4 h-4 mr-1" />Create EXTREME PO</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
